import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

interface Garcom {
  id: number;
  nome: string;
  email: string;
  telefone: string;
  empresa_id: number;
}

interface GarcomAuthContextData {
  garcom: Garcom | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, senha: string, lembrarMe?: boolean) => Promise<void>;
  switchUser: (email: string) => Promise<void>;
  logout: () => void;
}

const GarcomAuthContext = createContext<GarcomAuthContextData>({} as GarcomAuthContextData);

export const useGarcomAuth = () => {
  const context = useContext(GarcomAuthContext);
  if (!context) {
    throw new Error('useGarcomAuth deve ser usado dentro de um GarcomAuthProvider');
  }
  return context;
};

interface GarcomAuthProviderProps {
  children: React.ReactNode;
}

export const GarcomAuthProvider: React.FC<GarcomAuthProviderProps> = ({ children }) => {
  const [garcom, setGarcom] = useState<Garcom | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Função para verificar se o token expirou
  const verificarExpiracaoToken = (): boolean => {
    // Verificar primeiro no localStorage, depois no sessionStorage
    let tokenExpiration = localStorage.getItem('garcomTokenExpiration');
    if (!tokenExpiration) {
      tokenExpiration = sessionStorage.getItem('garcomTokenExpiration');
    }
    
    if (!tokenExpiration) {
      return false; // Se não há data de expiração, considerar expirado
    }
    
    const expirationDate = new Date(tokenExpiration);
    const agora = new Date();
    
    // Verificar se ainda não expirou (com margem de 1 minuto)
    return expirationDate > agora;
  };

  // Função para salvar token com data de expiração (7 dias se lembrarMe, senão apenas sessão)
  const salvarTokenComExpiracao = (token: string, lembrarMe: boolean = true) => {
    const agora = new Date();
    const expiracao = new Date(agora.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 dias
    
    if (lembrarMe) {
      // Salvar no localStorage para persistir por 7 dias
      localStorage.setItem('garcomToken', token);
      localStorage.setItem('garcomTokenExpiration', expiracao.toISOString());
    } else {
      // Salvar no sessionStorage apenas para a sessão atual
      sessionStorage.setItem('garcomToken', token);
      sessionStorage.setItem('garcomTokenExpiration', expiracao.toISOString());
    }
    
    // Configurar token no axios
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        // Tentar carregar do localStorage primeiro, depois do sessionStorage
        let storedToken = localStorage.getItem('garcomToken');
        let storedGarcom = localStorage.getItem('garcomUsuario');
        let storageType = 'localStorage';
        
        if (!storedToken || !storedGarcom) {
          storedToken = sessionStorage.getItem('garcomToken');
          storedGarcom = sessionStorage.getItem('garcomUsuario');
          storageType = 'sessionStorage';
        }

        if (storedToken && storedGarcom) {
          // Verificar se o token ainda não expirou
          if (verificarExpiracaoToken()) {
            setToken(storedToken);
            setGarcom(JSON.parse(storedGarcom));
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            console.log(`✅ [GARÇOM-AUTH] Sessão restaurada do ${storageType}`);
          } else {
            // Token expirado, limpar dados
            console.log('Token do garçom expirado. Fazendo logout automático.');
            localStorage.removeItem('garcomToken');
            localStorage.removeItem('garcomUsuario');
            localStorage.removeItem('garcomTokenExpiration');
            sessionStorage.removeItem('garcomToken');
            sessionStorage.removeItem('garcomUsuario');
            sessionStorage.removeItem('garcomTokenExpiration');
            delete api.defaults.headers.common['Authorization'];
          }
        }
      } catch (error) {
        console.error('Erro ao carregar autenticação do garçom:', error);
        localStorage.removeItem('garcomToken');
        localStorage.removeItem('garcomUsuario');
        localStorage.removeItem('garcomTokenExpiration');
        sessionStorage.removeItem('garcomToken');
        sessionStorage.removeItem('garcomUsuario');
        sessionStorage.removeItem('garcomTokenExpiration');
        delete api.defaults.headers.common['Authorization'];
      }
    };

    loadStoredAuth();
    setIsLoading(false);
  }, []);

  const login = async (email: string, senha: string, lembrarMe: boolean = true) => {
    try {
      console.log('🔐 [GARÇOM] Iniciando processo de login...');
      console.log('📧 [GARÇOM] Email:', email);
      console.log('🔑 [GARÇOM] Senha:', senha ? '***' : 'VAZIA');
      console.log('💾 [GARÇOM] Lembrar-me:', lembrarMe);
      
      console.log('🌐 [GARÇOM] Fazendo requisição para /api/garcom/login...');
      const response = await api.post('/api/garcom/login', { email, senha });
      
      console.log('✅ [GARÇOM] Resposta recebida:', response.status);
      console.log('📦 [GARÇOM] Dados da resposta:', response.data);
      
      const { token: novoToken, garcom: novoGarcom } = response.data;

      console.log('🔧 [GARÇOM] Configurando estado local...');
      setToken(novoToken);
      setGarcom(novoGarcom);

      // Salvar com data de expiração (7 dias se lembrarMe, senão apenas sessão)
      salvarTokenComExpiracao(novoToken, lembrarMe);
      if (lembrarMe) {
        localStorage.setItem('garcomUsuario', JSON.stringify(novoGarcom));
      } else {
        sessionStorage.setItem('garcomUsuario', JSON.stringify(novoGarcom));
      }

      console.log('✅ [GARÇOM] Login concluído com sucesso!');
      console.log('👤 [GARÇOM] Garçom logado:', novoGarcom);
    } catch (error: any) {
      console.error('❌ [GARÇOM] ERRO NO LOGIN:');
      console.error('📊 [GARÇOM] Tipo do erro:', typeof error);
      console.error('📋 [GARÇOM] Erro completo:', error);
      
      if (error.response) {
        console.error('🌐 [GARÇOM] Resposta do servidor:');
        console.error('   Status:', error.response.status);
        console.error('   Status Text:', error.response.statusText);
        console.error('   Headers:', error.response.headers);
        console.error('   Data:', error.response.data);
      } else if (error.request) {
        console.error('📡 [GARÇOM] Requisição enviada mas sem resposta:');
        console.error('   Request:', error.request);
      } else {
        console.error('⚙️ [GARÇOM] Erro na configuração da requisição:');
        console.error('   Message:', error.message);
      }
      
      console.error('🔍 [GARÇOM] Stack trace:', error.stack);
      
      const errorMessage = error.response?.data?.erro || error.message || 'Erro desconhecido ao fazer login';
      console.error('💬 [GARÇOM] Mensagem de erro final:', errorMessage);
      
      throw new Error(errorMessage);
    }
  };

  const switchUser = async (email: string) => {
    try {
      console.log('🔄 [GARÇOM] Solicitando troca rápida para:', email);
      const response = await api.post('/api/garcom/switch', { email });
      
      const { token: novoToken, garcom: novoGarcom } = response.data;

      setToken(novoToken);
      setGarcom(novoGarcom);

      // Usar a mesma preferência de persistência atual (se tem no localStorage, manter lá)
      const usarLocalStorage = !!localStorage.getItem('garcomUsuario');
      salvarTokenComExpiracao(novoToken, usarLocalStorage);
      
      if (usarLocalStorage) {
        localStorage.setItem('garcomUsuario', JSON.stringify(novoGarcom));
      } else {
        sessionStorage.setItem('garcomUsuario', JSON.stringify(novoGarcom));
      }

      console.log('✅ [GARÇOM] Troca concluída com sucesso para:', novoGarcom.nome);
    } catch (error: any) {
      console.error('❌ [GARÇOM] ERRO NA TROCA DE USUÁRIO:', error);
      const errorMessage = error.response?.data?.erro || error.message || 'Erro desconhecido ao trocar usuário';
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    setToken(null);
    setGarcom(null);
    // Limpar tanto localStorage quanto sessionStorage
    localStorage.removeItem('garcomToken');
    localStorage.removeItem('garcomUsuario');
    localStorage.removeItem('garcomTokenExpiration');
    sessionStorage.removeItem('garcomToken');
    sessionStorage.removeItem('garcomUsuario');
    sessionStorage.removeItem('garcomTokenExpiration');
    delete api.defaults.headers.common['Authorization'];
  };

  return (
    <GarcomAuthContext.Provider
      value={{
        garcom,
        token,
        isLoading,
        login,
        switchUser,
        logout
      }}
    >
      {children}
    </GarcomAuthContext.Provider>
  );
};
